import { emptyRemoteLink, restoreTask } from "@app/core";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { rehydrateStores } from "../lib/snapshot.js";
import { tr } from "../lib/tr.js";
import { toast } from "./toast.store.js";

/** A soft-deleted task/event row, surfaced in the Settings → Trash page. */
export interface TrashEntry {
  id: string;
  title: string;
  is_event: boolean;
  deleted_at: string;
  recurrence_id: string | null;
}

interface TrashState {
  loaded: boolean;
  busy: boolean;
  entries: TrashEntry[];
  hydrate: () => Promise<void>;
  restore: (id: string) => Promise<void>;
  purge: (id: string) => Promise<void>;
  emptyAll: () => Promise<void>;
}

export const useTrashStore = create<TrashState>((set, get) => ({
  loaded: false,
  busy: false,
  entries: [],

  async hydrate() {
    const { tasks } = getServices();
    const rows = await tasks.listAll();
    const entries: TrashEntry[] = rows
      .filter((t) => t.deleted_at != null)
      .map((t) => ({
        id: t.id,
        title: t.title,
        is_event: t.is_event,
        deleted_at: t.deleted_at as string,
        recurrence_id: t.recurrence_id,
      }))
      .sort((a, b) => b.deleted_at.localeCompare(a.deleted_at));
    set({ entries, loaded: true });
  },

  async restore(id) {
    const { tasks } = getServices();
    const row = await tasks.findById(id);
    if (!row) return;
    set({ busy: true });
    try {
      // A trashed task's remote (Google) event was deleted when it was trashed, so
      // its stored external link (external_id/etag/ical_uid) is STALE — it points at
      // an event that no longer exists. Clear it so the next sync push re-CREATES a
      // fresh event (insert path) instead of PATCHing the dead id. restoreTask
      // bumps updated_at so it's dirty for that push.
      // ("Delete all" only soft-deletes the base task; the recurrence rule stays
      // active, so clearing deleted_at also brings the whole series back.)
      // The unlink MUST go through setRemoteLink: `update` does not carry the link
      // columns (see TASK_REMOTE_LINK_FIELDS), so passing nulls through it is a
      // silent no-op, and setRemoteLink also bumps `link_rev` so the unlink beats
      // the stale link on every other device instead of being read as missing data.
      await tasks.setRemoteLink(id, emptyRemoteLink());
      await tasks.update(restoreTask({ ...row, ...emptyRemoteLink() }));
      await rehydrateStores();
      await get().hydrate();
      toast.success(tr(row.is_event ? "toast.trash.eventRestored" : "toast.trash.taskRestored"));
    } finally {
      set({ busy: false });
    }
  },

  async purge(id) {
    set({ busy: true });
    try {
      await purgeOne(id);
      await get().hydrate();
    } finally {
      set({ busy: false });
    }
  },

  async emptyAll() {
    const ids = get().entries.map((e) => e.id);
    set({ busy: true });
    try {
      // One overrides read shared across every purge — not one full-table scan
      // per trashed series.
      const overridesByRule = new Map<string, string[]>();
      for (const o of await getServices().recurrenceOverrides.listAll()) {
        const list = overridesByRule.get(o.recurrence_id) ?? [];
        list.push(o.id);
        overridesByRule.set(o.recurrence_id, list);
      }
      for (const id of ids) await purgeOne(id, overridesByRule);
      await get().hydrate();
    } finally {
      set({ busy: false });
    }
  },
}));

/** Permanently delete one task row plus, for a recurring base task, its now
 *  orphaned rule + overrides (one rule per series, so nothing else uses them).
 *  Pass `overridesByRule` (rule id → override ids) when purging many rows so
 *  the overrides table is scanned once, not per purge. */
async function purgeOne(id: string, overridesByRule?: Map<string, string[]>): Promise<void> {
  const { tasks, recurrence, recurrenceOverrides } = getServices();
  const row = await tasks.findById(id);
  await tasks.hardDelete(id);
  if (row?.recurrence_id) {
    const overrideIds = overridesByRule
      ? overridesByRule.get(row.recurrence_id) ?? []
      : (await recurrenceOverrides.listAll())
          .filter((o) => o.recurrence_id === row.recurrence_id)
          .map((o) => o.id);
    for (const oid of overrideIds) await recurrenceOverrides.hardDelete(oid);
    await recurrence.hardDelete(row.recurrence_id);
  }
}
